// src/features/kommunikation/components/ReplaceDocumentModal.tsx
//
// Modal für den stillen PDF-Austausch eines bestehenden communication_documents.
// Empfänger-Link, Passwort, Versandstatus UND der gespeicherte
// original_filename bleiben unverändert; aktualisiert werden über die
// Netlify Function replace-communication-document nur die Datei im Storage
// sowie file_size und updated_at.
//
// Vor dem Austausch wird der Dateiname der hochgeladenen Datei mit dem
// documentParserService geparst und gegen den bestehenden DB-Eintrag
// verglichen (Mandant, Personal, Monat, Jahr). Bei Mismatch wird der
// Austausch im UI verhindert.
//
// Sonderversand (ist_sonderversand = true): kein Monat/Jahr vorhanden. Hier
// wird der lenient Parser verwendet und nur Mandant + Personalnummer geprüft.

import React, { useState } from 'react';
import { Paperclip, AlertTriangle, Info, XCircle } from 'lucide-react';
import { CommunicationDocument } from '../types';
import { documentParserService } from '../services/DocumentParserService';

interface ReplaceDocumentModalProps {
  isOpen: boolean;
  doc: CommunicationDocument | null;
  onClose: () => void;
  onReplace: (documentId: string, file: File) => Promise<boolean>;
}

const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 4 MB

export const ReplaceDocumentModal: React.FC<ReplaceDocumentModalProps> = ({
  isOpen,
  doc,
  onClose,
  onReplace
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !doc) {
    return null;
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const selectedFile = e.target.files && e.target.files[0] ? e.target.files[0] : null;

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError('Bitte nur PDF-Dateien auswählen.');
      setFile(null);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setError(`Datei zu groß (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB). Maximum: 4 MB.`);
      setFile(null);
      return;
    }

    // Plausibilitätsprüfung: Dateiname muss zum bestehenden Eintrag passen.
    // Bei Sonderversand gibt es keinen Zeitraum; daher lenient parsen und nur
    // Mandant + Personalnummer prüfen.
    if (doc.ist_sonderversand) {
      const parsedSonder = documentParserService.parseFilenameSonderversand(selectedFile.name);

      if (!parsedSonder.isValid) {
        setError(`Dateiname kann nicht geparst werden: ${parsedSonder.error || 'Unbekannter Fehler'}`);
        setFile(null);
        return;
      }

      const sonderMismatches: string[] = [];

      if (parsedSonder.mandantennummer !== doc.mandantennummer) {
        sonderMismatches.push(
          `Mandantennummer im Dateinamen ist ${parsedSonder.mandantennummer}, erwartet ${doc.mandantennummer}`
        );
      }

      if (parsedSonder.personalnummer !== doc.personalnummer) {
        sonderMismatches.push(
          `Personalnummer im Dateinamen ist ${parsedSonder.personalnummer}, erwartet ${doc.personalnummer}`
        );
      }

      if (sonderMismatches.length > 0) {
        setError('Plausibilitätsprüfung fehlgeschlagen: ' + sonderMismatches.join('; '));
        setFile(null);
        return;
      }

      setFile(selectedFile);
      return;
    }

    // Regulärer Versand: strikter Parser inkl. Zeitraum-Vergleich.
    // Wir nutzen den selben Parser wie der Upload-Flow, damit Erwartungen an
    // das Filename-Schema homogen bleiben.
    const parsed = documentParserService.parseFilename(selectedFile.name);

    if (!parsed.isValid) {
      setError(`Dateiname kann nicht geparst werden: ${parsed.error || 'Unbekannter Fehler'}`);
      setFile(null);
      return;
    }

    const mismatches: string[] = [];

    if (parsed.mandantennummer !== doc.mandantennummer) {
      mismatches.push(
        `Mandantennummer im Dateinamen ist ${parsed.mandantennummer}, erwartet ${doc.mandantennummer}`
      );
    }

    if (parsed.personalnummer !== doc.personalnummer) {
      mismatches.push(
        `Personalnummer im Dateinamen ist ${parsed.personalnummer}, erwartet ${doc.personalnummer}`
      );
    }

    if (parsed.monat !== doc.monat || parsed.jahr !== doc.jahr) {
      const parsedZeitraum = `${String(parsed.monat).padStart(2, '0')}/${parsed.jahr}`;
      const expectedZeitraum = `${String(doc.monat).padStart(2, '0')}/${doc.jahr}`;
      mismatches.push(
        `Zeitraum im Dateinamen ist ${parsedZeitraum}, erwartet ${expectedZeitraum}`
      );
    }

    if (mismatches.length > 0) {
      setError('Plausibilitätsprüfung fehlgeschlagen: ' + mismatches.join('; '));
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Bitte zuerst eine PDF-Datei auswählen.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const success = await onReplace(doc.id, file);
      if (success) {
        setFile(null);
        onClose();
      } else {
        setError('Austausch fehlgeschlagen. Bitte erneut versuchen.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    setFile(null);
    setError(null);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={handleCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px',
            backgroundColor: '#0EA5E915', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Paperclip size={18} color="#0EA5E9" />
          </div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
            PDF austauschen
          </h2>
        </div>

        {/* Aktuelles Dokument */}
        <div
          style={{
            padding: '12px 14px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px'
          }}
        >
          <div style={{ marginBottom: '4px', color: '#111827' }}>
            <strong>Aktuelles Dokument:</strong> {doc.original_filename}
          </div>
          <div style={{ color: '#6B7280' }}>
            Mandant {doc.mandantennummer} · Personal {doc.personalnummer} ·{' '}
            {doc.ist_sonderversand
              ? 'Sonderversand'
              : (doc.monat != null && doc.jahr != null
                  ? documentParserService.formatMonthYear(doc.monat, doc.jahr)
                  : '–')} ·{' '}
            {(doc.file_size / 1024 / 1024).toFixed(2)} MB
          </div>
        </div>

        {/* Warnung bei bereits erfolgtem Zugriff */}
        {doc.zugegriffen_am && (
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400E',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start'
            }}
          >
            <AlertTriangle size={18} color="#F59E0B" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              Dieses Dokument wurde bereits {doc.zugriffs_count}× vom Empfänger abgerufen.
              Der Austausch ist trotzdem möglich; der Empfänger sieht beim nächsten Zugriff die neue Version.
            </span>
          </div>
        )}

        {/* Hinweis zum Verhalten */}
        <div
          style={{
            padding: '12px 14px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#1E40AF',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start'
          }}
        >
          <Info size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span>
            Empfänger-Link und Passwort bleiben unverändert. Es wird KEINE E-Mail versendet.
            Der gespeicherte Dateiname bleibt unverändert. Der Dateiname der neuen Datei muss
            zur Mandant- und Personalnummer sowie zum Zeitraum passen. Das alte PDF wird nicht
            gesichert und ist nach dem Austausch nicht wiederherstellbar.
          </span>
        </div>

        {/* File-Picker */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#374151'
            }}
          >
            Neue PDF-Datei (max. 4 MB)
          </label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileChange}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          {file && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
              Ausgewählt: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </div>

        {/* Fehleranzeige */}
        {error && (
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#991B1B',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start'
            }}
          >
            <XCircle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{error}</span>
          </div>
        )}

        {/* Action-Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={!file || submitting}
            style={{
              padding: '10px 20px',
              backgroundColor: !file || submitting ? '#9CA3AF' : '#0EA5E9',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: !file || submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Paperclip size={16} />
            {submitting ? 'Wird ausgetauscht...' : 'Austauschen'}
          </button>
        </div>
      </div>
    </div>
  );
};
